// react
import { ReactNode, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

// internationalization
import { useTranslation } from 'react-i18next';

// material ui icons
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import HdrAutoRoundedIcon from '@mui/icons-material/HdrAutoRounded';
import NotificationsActiveRoundedIcon from '@mui/icons-material/NotificationsActiveRounded';
import NotificationsOffRoundedIcon from '@mui/icons-material/NotificationsOffRounded';
import UndoRoundedIcon from '@mui/icons-material/UndoRounded';

// material ui
import {
  Box,
  Button,
  Card,
  ClickAwayListener,
  Grow,
  MenuItem,
  MenuList,
  Popper,
  Typography,
} from '@mui/material';

// api
import { ReadAlarmDto } from '@/api/generated/recalc/endpoints/v1/alarms';
import { useUpdateAlarmMutation } from '@/api/recalc/endpoints/alarmsApiSlice';

// components
import { showSnackbar } from '@/redux/snackbarSlice';

type AlarmStateValue = 0 | 1 | 3 | 4 | 5;

type AlarmStateKey =
  | 'Unresolved'
  | 'Reject'
  | 'Solved'
  | 'Reset'
  | 'AutoResolved';

type AlarmLike = Pick<ReadAlarmDto, 'AlarmId' | 'State'>;
type ApiAlarmState = Exclude<AlarmLike['State'], null | undefined>;

interface AlarmCellParams {
  value?: AlarmStateValue | AlarmStateKey | number | string | null;
  row?: {
    State?: AlarmStateValue | AlarmStateKey | number | string | null;
    state?: AlarmStateValue | AlarmStateKey | number | string | null;
    AlarmId?: AlarmLike['AlarmId'] | null;
    alarmId?: AlarmLike['AlarmId'] | null;
  } | null;
}

interface AlarmStateSelectProps {
  params?: AlarmCellParams | null;
  alarm?: AlarmLike | null;
  disabled?: boolean;
}

// Create a lookup object to map string states to numeric values
const alarmStateValueMap = {
  Unresolved: 0,
  Reject: 1,
  Solved: 3,
  Reset: 4,
  AutoResolved: 5,
} as const;

const alarmStateValues: AlarmStateValue[] = [0, 1, 3, 4, 5];
const alarmStateApiValueMap: Record<AlarmStateValue, ApiAlarmState> = {
  0: 'Unresolved',
  1: 'Reject',
  3: 'Solved',
  4: 'Reset',
  5: 'AutoResolved',
};

const toAlarmStateValue = (state: unknown): AlarmStateValue => {
  if (
    typeof state === 'number' &&
    alarmStateValues.includes(state as AlarmStateValue)
  ) {
    return state as AlarmStateValue;
  }

  if (typeof state === 'string') {
    if (state in alarmStateValueMap) {
      return alarmStateValueMap[state as AlarmStateKey];
    }

    const numericState = Number(state);
    if (alarmStateValues.includes(numericState as AlarmStateValue)) {
      return numericState as AlarmStateValue;
    }
  }

  return 0;
};

const getAlarmStateIcon = (state: AlarmStateValue): ReactNode => {
  const iconMap: Record<AlarmStateValue, ReactNode> = {
    0: (
      <NotificationsActiveRoundedIcon
        fontSize="small"
        sx={{ color: '#FF6347' }}
      />
    ), // Unresolved
    1: (
      <NotificationsOffRoundedIcon fontSize="small" sx={{ color: '#808080' }} />
    ), // Reject
    3: <CheckCircleRoundedIcon fontSize="small" sx={{ color: '#32CD32' }} />, // Solved
    4: <UndoRoundedIcon fontSize="small" sx={{ color: '#1E90FF' }} />, // Reset
    5: <HdrAutoRoundedIcon fontSize="small" sx={{ color: '#9932CC' }} />, // AutoResolved
  };

  return iconMap[state];
};

const AlarmStateSelect = ({
  params,
  alarm,
  disabled = false,
}: AlarmStateSelectProps) => {
  const { t } = useTranslation('alarms');
  const dispatch = useDispatch();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const anchorRef = useRef<HTMLButtonElement | null>(null);
  const open = Boolean(anchorEl);

  const [updateAlarm] = useUpdateAlarmMutation();

  const handleAlarmUpdate = async (
    alarmId: AlarmLike['AlarmId'],
    updatedAlarm: Partial<AlarmLike>,
  ) => {
    if (alarmId == null) {
      return;
    }

    const result = await updateAlarm({ alarmId, alarm: updatedAlarm });

    if (!('error' in result)) {
      dispatch(
        showSnackbar({
          message: t('messages.alarm_updated'),
          severity: 'success',
        }),
      );
    }
    // Errors are handled globally by apiErrorMiddleware (snackbar + App Insights).
  };

  const alarmStates: Array<{ value: AlarmStateValue; label: string }> = [
    { value: 0, label: t('state.unresolved') },
    { value: 1, label: t('state.dismissed') },
    { value: 3, label: t('state.resolved') },
    { value: 4, label: t('state.reset') },
    { value: 5, label: t('state.auto_resolved') },
  ];

  // Handle both DataGrid usage (params) and direct usage (alarm prop)
  let currentState: AlarmStateValue;
  let alarmId: AlarmLike['AlarmId'] | null;

  if (params) {
    // DataGrid usage
    currentState = toAlarmStateValue(
      params.value ?? params.row?.state ?? params.row?.State,
    );
    alarmId = params.row?.alarmId ?? params.row?.AlarmId ?? null;
  } else if (alarm) {
    // Direct usage (like in AlarmInfoCard)
    currentState = toAlarmStateValue(alarm.State);
    alarmId = alarm.AlarmId ?? null;
  } else {
    currentState = 0;
    alarmId = null;
  }

  const handleToggle = (event: React.MouseEvent<HTMLElement>) => {
    if (disabled) return;
    event.stopPropagation();
    setAnchorEl(open ? null : anchorRef.current);
  };

  const handleClose = () => setAnchorEl(null);

  const handleStateChange = (newStateValue: AlarmStateValue) => {
    handleAlarmUpdate(alarmId, {
      State: alarmStateApiValueMap[newStateValue],
    });
    handleClose();
  };

  const availableStates = alarmStates.filter(
    (state) => state.value !== currentState && state.value !== 5, // Exclude current state and AutoResolved
  );

  return (
    <div onClick={(e) => e.stopPropagation()}>
      <Button
        variant="contained"
        ref={anchorRef}
        aria-label="alarm state button"
        startIcon={getAlarmStateIcon(currentState)}
        endIcon={
          !disabled && availableStates.length > 0 ? <ArrowDropDownIcon /> : null
        }
        onClick={handleToggle}
        sx={{
          // textTransform: 'none',
          // borderColor: 'divider',
          // ':hover': {
          //   borderColor: disabled ? 'divider' : 'primary.main',
          //   backgroundColor: disabled ? 'transparent' : 'action.hover',
          // },
          cursor:
            disabled || availableStates.length === 0 ? 'default' : 'pointer',
        }}
        style={{ minWidth: 100 }}
      >
        {alarmStates.find((state) => state.value === currentState)?.label ||
          t('state.unresolved')}
      </Button>

      {!disabled && availableStates.length > 0 && (
        <Popper
          open={open}
          anchorEl={anchorEl}
          role={undefined}
          transition
          style={{ zIndex: 1301 }}
        >
          {({ TransitionProps }) => (
            <Grow {...TransitionProps}>
              <Card variant="outlined">
                <ClickAwayListener onClickAway={handleClose}>
                  <MenuList
                    id="alarm-state-menu"
                    autoFocusItem
                    dense
                    sx={{ p: 0.5 }}
                  >
                    {availableStates.map((state) => (
                      <MenuItem
                        key={state.value}
                        onClick={() => handleStateChange(state.value)}
                      >
                        <Box
                          component="span"
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                          }}
                        >
                          <Box sx={{ mr: 1 }}>
                            {getAlarmStateIcon(state.value)}
                          </Box>
                          <Typography variant="body2" fontWeight="medium">
                            {state.label}
                          </Typography>
                        </Box>
                      </MenuItem>
                    ))}
                  </MenuList>
                </ClickAwayListener>
              </Card>
            </Grow>
          )}
        </Popper>
      )}
    </div>
  );
};

export default AlarmStateSelect;
